/** Connected native dry-country incision, built before the final river graph.
 * Descriptors never query rivers. Their descending floor is the shared solid
 * surface; eligible coastal routes may subsequently receive a narrow stream. */
import {hashSeed,rngFromString} from './rng.js';
import * as pmath from './pmath.js';
import type {PlanetDef} from './universe.js';
type V3=readonly[number,number,number];
export interface CanyonNative {heightM:number;temp:number;moist:number;plateau:number;butteRock:number}
export interface CanyonPoint {
 readonly x:number;readonly y:number;readonly z:number;readonly s:number;
 readonly floorM:number;readonly nativeM:number;readonly floorHalfM:number;readonly outerHalfM:number;readonly depthM:number;
 readonly left:number;readonly right:number;
 readonly leftProfile:readonly number[];readonly rightProfile:readonly number[];
 /** Post-routing fracture sections. The accepted floor and footprint remain
  * independent of changes to the exposed-wall grammar. */
 readonly leftFractures?:readonly number[];readonly rightFractures?:readonly number[];
 readonly fractureWeight?:number;readonly junctionGuard?:number;
}
export interface CanyonSegment {readonly center:V3;readonly radiusM:number;readonly depthM:number;readonly index:number}
export interface CanyonButtress {
 readonly center:V3;readonly along:V3;readonly across:V3;readonly radiusM:number;
 /** Convex plan half-planes, flattened nx,ny,d triples in a fixed local frame. */
 readonly planes:readonly number[];readonly baseM:number;readonly topM:number;readonly footM:number;
 readonly tiltAlong:number;readonly tiltAcross:number;
}
export interface CanyonDef {
 readonly id:string;readonly seed:number;readonly points:readonly CanyonPoint[];readonly segments:readonly CanyonSegment[];
 readonly center:V3;readonly radiusM:number;readonly depthM:number;
 readonly parentId:string|null;readonly joinS:number|null;readonly wet:boolean;
 readonly buttresses?:readonly CanyonButtress[];
}
export interface CanyonSample {heightM:number;rock:number;weight:number}
export const canyonSample=():CanyonSample=>({heightM:0,rock:0,weight:0});
export interface CanyonLocation {x:number;y:number;z:number;s:number;floorM:number;nativeM:number;floorHalfM:number;outerHalfM:number;depthM:number}
export const canyonLocation=():CanyonLocation=>({x:0,y:0,z:0,s:0,floorM:0,nativeM:0,floorHalfM:0,outerHalfM:0,depthM:0});
const smooth=(v:number):number=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t)};
const norm=(x:number,y:number,z:number):V3=>{const l=Math.sqrt(x*x+y*y+z*z);return[x/l,y/l,z/l]};
const dot=(a:V3,b:V3):number=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const cross=(a:V3,b:V3):V3=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
const chord=(a:V3,b:V3,R:number):number=>Math.sqrt(Math.max(0,2-2*dot(a,b)))*R;
/** Same segment parameter used by terrain and adopted water. */
export function canyonPoint(f:CanyonDef,s:number,out:CanyonLocation):CanyonLocation {
 const points=f.points;let lo=0,hi=points.length-1;
 while(hi-lo>1){const m=(lo+hi)>>>1;if(points[m].s<s)lo=m;else hi=m;}
 const a=points[lo],b=points[hi],t=Math.max(0,Math.min(1,(s-a.s)/(b.s-a.s))),x=a.x+(b.x-a.x)*t,y=a.y+(b.y-a.y)*t,z=a.z+(b.z-a.z)*t,l=Math.sqrt(x*x+y*y+z*z);
 out.x=x/l;out.y=y/l;out.z=z/l;out.s=a.s+(b.s-a.s)*t;out.floorM=a.floorM+(b.floorM-a.floorM)*t;out.nativeM=a.nativeM+(b.nativeM-a.nativeM)*t;
 out.floorHalfM=a.floorHalfM+(b.floorHalfM-a.floorHalfM)*t;out.outerHalfM=a.outerHalfM+(b.outerHalfM-a.outerHalfM)*t;out.depthM=a.depthM+(b.depthM-a.depthM)*t;return out;
}
/** Nonperiodic construction-time field for unequal wall ledges. */
function field(s:number,seed:number):number {
 const k=Math.floor(s),t=smooth(s-k),hash=(i:number)=>{let h=Math.imul(i,374761393)^seed;h=Math.imul(h^(h>>>13),1274126177);return((h^(h>>>16))>>>0)/4294967295;};return hash(k)*(1-t)+hash(k+1)*t;
}
function jointField(s:number,seed:number):number {
 const k=Math.floor(s),t=s-k,hash=(i:number)=>{let h=Math.imul(i,374761393)^seed;h=Math.imul(h^(h>>>13),1274126177);return((h^(h>>>16))>>>0)/4294967295;};return hash(k)*(1-t)+hash(k+1)*t;
}
/** Broad planar blocks with short diagonal joints, not fine surface noise. */
function blockField(s:number,seed:number):number {
 const k=Math.floor(s),f=s-k,t=Math.max(0,Math.min(1,(f-.18)/.40));
 const hash=(i:number)=>{let h=Math.imul(i,374761393)^seed;h=Math.imul(h^(h>>>13),1274126177);return((h^(h>>>16))>>>0)/4294967295;};
 return hash(k)*(1-t)+hash(k+1)*t;
}
function applySection(f:CanyonDef,R:number,best:number,bestT:number,bestRawT:number,bestD2:number,dx:number,dy:number,dz:number,heightM:number,out:CanyonSample):void {
   if(best<0)return;const a=f.points[best],b=f.points[best+1],t=bestT,bx=b.x-a.x,by=b.y-a.y,bz=b.z-a.z;
   const px=dx-a.x-bx*t,py=dy-a.y-by*t,pz=dz-a.z-bz*t,dist=Math.sqrt(bestD2)*R,outer=a.outerHalfM+(b.outerHalfM-a.outerHalfM)*t;if(dist>=outer)return;
   const floor=a.floorM+(b.floorM-a.floorM)*t,half=a.floorHalfM+(b.floorHalfM-a.floorHalfM)*t;
   const signed=(px*(by*dz-bz*dy)+py*(bz*dx-bx*dz)+pz*(bx*dy-by*dx));
   const side=signed<0?a.left+(b.left-a.left)*t:a.right+(b.right-a.right)*t;
   const pa=signed<0?a.leftProfile:a.rightProfile,pb=signed<0?b.leftProfile:b.rightProfile;
   const x=Math.max(0,dist-half)/side,talus=pa[0]+(pb[0]-pa[0])*t,wall1=pa[1]+(pb[1]-pa[1])*t,ledge=pa[2]+(pb[2]-pa[2])*t,wall2=pa[3]+(pb[3]-pa[3])*t,rim=pa[4]+(pb[4]-pa[4])*t;
   const talusH=pa[5]+(pb[5]-pa[5])*t,ledgeH=pa[6]+(pb[6]-pa[6])*t,upperH=pa[7]+(pb[7]-pa[7])*t;
   let rise:number;
   if(x<=0)rise=0;
   else if(x<talus)rise=talusH*x/talus;
   else if(x<talus+wall1)rise=talusH+(ledgeH-talusH)*(x-talus)/wall1;
   else if(x<talus+wall1+ledge)rise=ledgeH+.025*(x-talus-wall1)/ledge;
   else if(x<talus+wall1+ledge+wall2)rise=ledgeH+.025+(upperH-ledgeH-.025)*(x-talus-wall1-ledge)/wall2;
   else rise=upperH+(1-upperH)*smooth((x-talus-wall1-ledge-wall2)/rim);
   let exposed=smooth((dist-half-2)/(talus*side*.7))*(1-smooth((x-talus-wall1-ledge-wall2)/rim));
   const fa=signed<0?a.leftFractures:a.rightFractures,fb=signed<0?b.leftFractures:b.rightFractures;
   if(fa&&fb){
    const strength=(a.fractureWeight??0)+((b.fractureWeight??0)-(a.fractureWeight??0))*t;
    if(strength>0&&x>0){
     const foot=fa[0]+(fb[0]-fa[0])*t,lower=fa[1]+(fb[1]-fa[1])*t,shelfA=fa[2]+(fb[2]-fa[2])*t,middle=fa[3]+(fb[3]-fa[3])*t,
      shelfB=fa[4]+(fb[4]-fa[4])*t,upper=fa[5]+(fb[5]-fa[5])*t,shoulder=fa[6]+(fb[6]-fa[6])*t,
      footH=fa[7]+(fb[7]-fa[7])*t,lowH=fa[8]+(fb[8]-fa[8])*t,highH=fa[9]+(fb[9]-fa[9])*t,capH=fa[10]+(fb[10]-fa[10])*t;
     let broken:number;
     if(x<foot)broken=footH*x/foot;
     else if(x<foot+lower)broken=footH+(lowH-footH)*(x-foot)/lower;
     else if(x<foot+lower+shelfA)broken=lowH+.018*(x-foot-lower)/shelfA;
     else if(x<foot+lower+shelfA+middle)broken=lowH+.018+(highH-lowH-.018)*(x-foot-lower-shelfA)/middle;
     else if(x<foot+lower+shelfA+middle+shelfB)broken=highH+.018*(x-foot-lower-shelfA-middle)/shelfB;
     else if(x<foot+lower+shelfA+middle+shelfB+upper)broken=highH+.018+(capH-highH-.018)*(x-foot-lower-shelfA-middle-shelfB)/upper;
     else broken=capH+(1-capH)*Math.min(1,(x-foot-lower-shelfA-middle-shelfB-upper)/shoulder);
     // At accepted joins, keep new buttresses but do not deepen the other
     // route's established floor. The original query remains the lower bound.
     const guard=(a.junctionGuard??0)+((b.junctionGuard??0)-(a.junctionGuard??0))*t;
     rise+=(broken-rise)*strength*(broken<rise?1-guard:1);
     const fractureExposed=smooth((x-foot*.30)/Math.max(2,foot*.55))*(1-smooth((x-foot-lower-shelfA-middle-shelfB-upper)/shoulder));
     exposed+=(fractureExposed-exposed)*strength;
    }
   }
   let end=1;
   if(best===0&&bestRawT<0)end=1-smooth(-bestRawT*(b.s-a.s)/half);
   if(best===f.points.length-2&&bestRawT>1)end=1-smooth((bestRawT-1)*(b.s-a.s)/half);
   const weight=(1-Math.min(1,rise))*end;if(weight<=0)return;
   const target=heightM+(floor-heightM)*weight;
   // A shared floor may fill up to3m of native micro-relief, already validated
   // at construction. Junction union chooses the lower connected corridor.
   if(out.weight===0||target<out.heightM)out.heightM=target;
   const rock=exposed*smooth(Math.max(0,heightM-floor)/25)*end;
   out.rock=Math.max(out.rock,rock);out.weight=Math.max(out.weight,weight);
}
/** Brute-force route query used only for construction-time junction validation. */
function routeSample(f:CanyonDef,R:number,dx:number,dy:number,dz:number,heightM:number,out:CanyonSample):CanyonSample {
 out.heightM=heightM;out.rock=out.weight=0;let bestD2=Infinity;
 for(let i=0;i<f.points.length-1;i++){
  const a=f.points[i],b=f.points[i+1],ax=dx-a.x,ay=dy-a.y,az=dz-a.z,bx=b.x-a.x,by=b.y-a.y,bz=b.z-a.z,ab2=bx*bx+by*by+bz*bz;
  const t=Math.max(0,Math.min(1,(ax*bx+ay*by+az*bz)/ab2)),px=ax-bx*t,py=ay-by*t,pz=az-bz*t;
  bestD2=Math.min(bestD2,px*px+py*py+pz*pz);
 }
 const distance=Math.sqrt(bestD2)*R,limit=distance+Math.max(.002,Math.min(8,distance*.30)),limit2=limit*limit/(R*R),scratch=canyonSample();
 let sum=0,height=0,rock=0,weight=0;
 for(let i=0;i<f.points.length-1;i++){
  const a=f.points[i],b=f.points[i+1],ax=dx-a.x,ay=dy-a.y,az=dz-a.z,bx=b.x-a.x,by=b.y-a.y,bz=b.z-a.z,ab2=bx*bx+by*by+bz*bz;
  const raw=(ax*bx+ay*by+az*bz)/ab2,t=Math.max(0,Math.min(1,raw)),px=ax-bx*t,py=ay-by*t,pz=az-bz*t,d2=px*px+py*py+pz*pz;if(d2>=limit2)continue;
  const q=limit-Math.sqrt(d2)*R,k=q*q;scratch.heightM=heightM;scratch.rock=scratch.weight=0;
  applySection(f,R,i,t,raw,d2,dx,dy,dz,heightM,scratch);sum+=k;height+=scratch.heightM*k;rock+=scratch.rock*k;weight+=scratch.weight*k;
 }
 if(sum>0){out.heightM=height/sum;out.rock=rock/sum;out.weight=weight/sum;}return out;
}

interface SegmentRef {f:CanyonDef;i:number;bx:number;by:number;bz:number;ab2:number}
export class CanyonTerrain {
 readonly enabled:boolean;readonly features:readonly CanyonDef[];
 private readonly cells=new Map<number,readonly SegmentRef[]>();private readonly cellM:number;private readonly gridSize:number;private readonly sectionScratch=canyonSample();
 private readonly blockCells=new Map<number,readonly CanyonButtress[]>();
 // One reusable double-precision projection per bucket entry. The nearest
 // distance and blending passes consume exactly the same rounded values.
 private projections=new Float64Array(0);
 constructor(private readonly def:PlanetDef,native:(x:number,y:number,z:number,out:CanyonNative)=>CanyonNative,macro:(x:number,y:number,z:number)=>number){
  this.enabled=def.terrain.amplitudeRel>=.001&&def.atmosphere!==null&&(def.archetype==='desert'||((def.archetype==='terran'||def.archetype==='ocean')&&def.hasFlora));
  this.cellM=Math.min(600,def.radius*.08);this.gridSize=Math.ceil(2*def.radius/this.cellM)+3;
  const features:CanyonDef[]=[];this.features=features;if(!this.enabled){Object.freeze(features);return;}
  const R=def.radius,sea=def.seaRadius===null?-1e9:def.seaRadius-R,scale=Math.min(1,R/12000),step=80*scale,maxDepth=150*scale;
  const scratch:CanyonNative={heightM:0,temp:0,moist:0,plateau:0,butteRock:0};
  const get=(d:V3)=>({...native(...d,scratch)});
  const sources:{d:V3;h:number;key:number}[]=[],M=384,phase=rngFromString(`${def.seed}:canyon-sites:v1`)()*pmath.TWO_PI;
  for(let i=0;i<M;i++){
   const y=1-2*(i+.5)/M,a=phase+i*2.399963229728653,r=Math.sqrt(1-y*y),d:V3=[r*pmath.cos(a),y,r*pmath.sin(a)],n=get(d);
   if(n.temp>.10&&n.moist<-.17&&n.plateau<.035&&n.butteRock<.01&&n.heightM>sea+100*scale)sources.push({d,h:n.heightM,key:i});
  }
  sources.sort((a,b)=>b.h-a.h);
  const make=(route:V3[],key:string,parent:CanyonDef|null,joinS:number|null,joinFloor:number|null,wet:boolean):CanyonDef|null=>{
   if(route.length<10)return null;
   const dirs:V3[]=[],arc:number[]=[],terrain:CanyonNative[]=[];let length=0;
   for(let i=0;i<route.length-1;i++){
    const a=route[i],b=route[i+1],n=Math.max(1,Math.ceil(chord(a,b,R)/(20*scale)));
    for(let j=0;j<n;j++){const t=j/n,d=norm(a[0]+(b[0]-a[0])*t,a[1]+(b[1]-a[1])*t,a[2]+(b[2]-a[2])*t);if(dirs.length)length+=chord(dirs[dirs.length-1],d,R);dirs.push(d);arc.push(length);terrain.push(get(d));}
   }
   const last=route[route.length-1];length+=chord(dirs[dirs.length-1],last,R);dirs.push(last);arc.push(length);terrain.push(get(last));
   if(length<(parent?650:1500)*scale)return null;
   const seed=hashSeed(`${def.seed}:canyon:${key}`),floor:number[]=new Array(dirs.length),depth=(88+rngFromString(`${seed}:depth`)()*62)*scale;
   floor[floor.length-1]=joinFloor??terrain[terrain.length-1].heightM;
   const endNative=terrain[terrain.length-1];if(endNative.plateau>.08||endNative.butteRock>.2||endNative.temp<.045||endNative.moist>-.08)return null;
   let incised=0,maxCut=0;
   for(let i=floor.length-2;i>=0;i--){
    const s=arc[i],n=terrain[i],tail=parent?1:smooth((length-s)/(550*scale)),head=smooth(s/(350*scale));
    const desired=n.heightM-depth*head*tail;
    floor[i]=Math.max(desired,floor[i+1]+(arc[i+1]-s)*.001);
    if(floor[i]>n.heightM+3*scale||n.plateau>.08||n.butteRock>.2||n.temp<.045||n.moist>-.08)return null;
    const cut=n.heightM-floor[i];if(cut>45*scale)incised++;maxCut=Math.max(maxCut,cut);
   }
   if(maxCut<65*scale||incised<25)return null;
   const points:CanyonPoint[]=dirs.map((d,i)=>{
    const s=arc[i],cut=Math.max(0,terrain[i].heightM-floor[i]),half=(12+field(s/(700*scale),seed^0x464c4f52)*12)*scale;
    const left=.78+.44*field(s/(410*scale),seed^0x4c454654),right=.78+.44*field(s/(570*scale),seed^0x52494748);
    const profile=(salt:number)=>{
     const a=jointField(s/(83*scale),seed^salt),b=jointField(s/(137*scale)+.4,seed^(salt+17)),c=jointField(s/(179*scale)+.7,seed^(salt+71)),e=jointField(s/(109*scale)+.2,seed^(salt+131));
     const talusH=.065+.065*a,ledgeH=.26+.32*c,upperH=.79+.13*e;
     // Exposed faces target steep70–80degree grades. Broad traversable talus
     // and occasional deep setbacks are separate structural sections.
     return Object.freeze([(10+14*a)*scale+cut*.035,Math.max(3.5*scale,(ledgeH-talusH)*cut/(3.6+1.7*b)),(.8+54*c*c*c)*scale,
      Math.max(3.5*scale,(upperH-ledgeH-.025)*cut/(3.8+1.8*e)),(19+21*b)*scale+cut*.08,talusH,ledgeH,upperH]);
    };
    const leftProfile=profile(0x1ee7),rightProfile=profile(0x713f);
    // Bounds use the sum of independent structural maxima, not endpoint
    // products of width and side scale: those can
    // peak between knots when the two factors vary in opposite directions.
    return Object.freeze({x:d[0],y:d[1],z:d[2],s,floorM:floor[i],nativeM:terrain[i].heightM,floorHalfM:half,outerHalfM:half+(127*scale+cut*.43)*1.22+2,depthM:cut,left,right,leftProfile,rightProfile});
   });
   const segments:CanyonSegment[]=points.slice(0,-1).map((a,i)=>{const b=points[i+1],center=norm(a.x+b.x,a.y+b.y,a.z+b.z),radiusM=(b.s-a.s)*.5+Math.max(a.outerHalfM,b.outerHalfM)+18;return Object.freeze({center:Object.freeze(center),radiusM,depthM:Math.max(a.depthM,b.depthM),index:i});});
   const center=norm(...dirs[Math.floor(dirs.length/2)]),radiusM=Math.max(...segments.map(s=>chord(center,s.center,R)+s.radiusM));
   // Independent networks must not excavate one another's floors. A named
   // tributary may overlap its host only in the immediate junction approach.
   for(const other of features){
    if(chord(center,other.center,R)>=radiusM+other.radiusM)continue;
    for(const seg of segments)for(const host of other.segments){
     if(chord(seg.center,host.center,R)>=seg.radiusM+host.radiusM)continue;
     if(parent===other&&points[seg.index].s>length-500*scale&&Math.abs(other.points[host.index].s-(joinS??0))<500*scale)continue;
     return null;
    }
   }
   const result:CanyonDef=Object.freeze({id:`${def.id}:canyon:${key}`,seed,points:Object.freeze(points),segments:Object.freeze(segments),center:Object.freeze(center),radiusM,depthM:maxCut,parentId:parent?.id??null,joinS,wet});
   if(parent){
    const loc=canyonLocation(),hit=canyonSample();
    for(const route of[result,parent]){
     const other=route===result?parent:result,total=route.points[route.points.length-1].s;
     const lo=route===result?Math.max(0,total-550*scale):Math.max(0,(joinS??0)-550*scale),hi=route===result?total:Math.min(total,(joinS??0)+550*scale);
     for(let s=lo;s<=hi;s+=5*scale){
      canyonPoint(route,s,loc);const h=native(loc.x,loc.y,loc.z,scratch).heightM;
      routeSample(other,R,loc.x,loc.y,loc.z,h,hit);
      if(hit.weight>0&&hit.heightM<loc.floorM-.035)return null;
     }
    }
   }
   return result;
  };
  let attempts=0;const maxTrunks=def.archetype==='desert'?4:3;
  for(const source of sources){
   if(features.filter(f=>f.parentId===null).length>=maxTrunks||attempts>=32)break;
   if(features.some(f=>chord(f.center,source.d,R)<f.radiusM+800*scale))continue;attempts++;
   const route:V3[]=[source.d];let current=source.d,previous:V3|null=null,wet=false,lowest=source.h,lowestIndex=0,stale=0;
   for(let j=0;j<120;j++){
    const east=norm(current[2],0,-current[0]),north=cross(current,east);let best:V3|null=null,bestDir:V3|null=null,bestCost=Infinity;
    const bearing=(field(j*step/(900*scale),hashSeed(`${def.seed}:canyon-bearing:${source.key}`))-.5)*.5;
    for(let k=0;k<24;k++){
     const a=k*pmath.TWO_PI/24+bearing,co=pmath.cos(a),si=pmath.sin(a),dir:V3=[east[0]*co+north[0]*si,east[1]*co+north[1]*si,east[2]*co+north[2]*si],turn=previous?dot(dir,previous):1;if(turn<.45)continue;
     const d=norm(current[0]+dir[0]*step/R,current[1]+dir[1]*step/R,current[2]+dir[2]*step/R),n=get(d);
     if(n.plateau>.08||n.butteRock>.2||n.temp<.035||n.moist>-.04)continue;
     const cost=macro(...d)+n.heightM*.12+(1-turn)*.7*scale;
     if(cost<bestCost){best=d;bestDir=dir;bestCost=cost;}
    }
    if(!best||!bestDir)break;
    if(route.slice(0,-8).some(d=>chord(d,best!,R)<step*.9))break;
    route.push(best);current=best;previous=bestDir;const h=get(best).heightM;
    if(h<lowest){lowest=h;lowestIndex=route.length-1;stale=0;}else stale++;
    if(def.seaRadius!==null&&h<sea-.8){wet=def.terrain.riverDensity>0;lowestIndex=route.length-1;break;}
    if(stale>22||h-lowest>maxDepth*.8)break;
   }
   if(!wet)route.length=lowestIndex+1;
   const trunk=make(route,`t${source.key}`,null,null,null,wet);if(!trunk)continue;features.push(trunk);
   // A tributary shares an exact parent knot/floor. It is accepted only when
   // the same backwards monotonic construction can reach that junction.
   const joins:{index:number;grade:number}[]=[];
   for(let i=Math.floor(trunk.points.length*.3);i<trunk.points.length*.76;i+=4){
    const a=trunk.points[Math.max(0,i-8)],b=trunk.points[Math.min(trunk.points.length-1,i+8)];
    joins.push({index:i,grade:(a.floorM-b.floorM)/(b.s-a.s)});
   }
   joins.sort((a,b)=>a.grade-b.grade||a.index-b.index);
   const selected:number[]=[];for(const j of joins){if(selected.every(i=>Math.abs(trunk.points[i].s-trunk.points[j.index].s)>160*scale))selected.push(j.index);if(selected.length===3)break;}
   let branch:CanyonDef|null=null;
   for(const joinIndex of selected){
    const join=trunk.points[joinIndex],prior=trunk.points[Math.max(0,joinIndex-2)],jd:V3=[join.x,join.y,join.z],tangent=norm(join.x-prior.x,join.y-prior.y,join.z-prior.z),side=norm(...cross(jd,tangent));
    for(const sign of[-1,1]){
     const branchRoute:V3[]=[];
     for(let j=0;j<=16;j++){const t=1-j/16,sideM=sign*1200*scale*t,upM=-700*scale*t*t;branchRoute.push(norm(jd[0]+(side[0]*sideM+tangent[0]*upM)/R,jd[1]+(side[1]*sideM+tangent[1]*upM)/R,jd[2]+(side[2]*sideM+tangent[2]*upM)/R));}
     branch=make(branchRoute,`b${source.key}:${sign}`,trunk,join.s,join.floorM,trunk.wet);if(branch)break;
    }if(branch)break;
   }
   if(branch)features.push(branch);
  }
  // Wall detail is attached only after the topology is accepted. It cannot
  // alter source selection, tributary acceptance, route IDs, or floor heights.
  for(let fi=0;fi<features.length;fi++){
   const f=features[fi],length=f.points[f.points.length-1].s,joins=features.filter(other=>other.parentId===f.id).map(other=>other.joinS!);
   const points=f.points.map(p=>{
    let junctionGuard=f.parentId?1-smooth((length-p.s-550*scale)/(140*scale)):0;
    for(const join of joins)junctionGuard=Math.max(junctionGuard,1-smooth((Math.abs(p.s-join)-550*scale)/(140*scale)));
    const fractureWeight=smooth((p.depthM-20*scale)/(30*scale));
    const fractures=(salt:number)=>{
     const block=blockField(p.s/(76*scale),f.seed^salt),recess=blockField(p.s/(113*scale)+.37,f.seed^(salt+43)),stepA=blockField(p.s/(59*scale)+.61,f.seed^(salt+89)),stepB=blockField(p.s/(97*scale)+.19,f.seed^(salt+157));
     const footH=.09+.06*recess,lowH=.27+.12*stepB,highH=.60+.12*block,capH=.88+.105*stepA;
     // Tall narrow faces move laterally by tens of metres between rock blocks.
     // Shelves pinch almost closed between isolated broad shelves; their two
     // independent outlines cannot form parallel continuous terrace bands.
     const widths=[(4+34*recess*recess)*scale,Math.max(1.4*scale,(lowH-footH)*p.depthM/(5.2+2*block)),
      (.35+41*stepA*stepA*stepA)*scale,Math.max(1.4*scale,(highH-lowH-.018)*p.depthM/(5+2*recess)),
      (.35+36*stepB*stepB*stepB)*scale,Math.max(1.4*scale,(capH-highH-.018)*p.depthM/(5.4+2*stepA)),(9+19*block)*scale];
     const total=widths.reduce((sum,w)=>sum+w,0),budget=(p.outerHalfM-p.floorHalfM-2)/1.22,factor=Math.min(1,budget/total);
     for(let i=0;i<widths.length;i++)widths[i]*=factor;
     return Object.freeze([...widths,footH,lowH,highH,capH]);
    };
    return Object.freeze({...p,fractureWeight,junctionGuard,leftFractures:fractures(0x4319),rightFractures:fractures(0x9e71)});
   });
   const blocks:CanyonButtress[]=[],rng=rngFromString(`${f.seed}:wall-blocks:v1`),loc=canyonLocation(),prior=canyonLocation(),next=canyonLocation();
   for(const sign of[-1,1]){
    for(let s=(70+rng()*65)*scale;s<length-90*scale;s+=(72+rng()*74)*scale){
     canyonPoint(f,s,loc);if(loc.depthM<48*scale)continue;
     canyonPoint(f,s-2*scale,prior);canyonPoint(f,s+2*scale,next);
     const radial:V3=[loc.x,loc.y,loc.z],along=norm(next.x-prior.x,next.y-prior.y,next.z-prior.z),outward=cross(radial,along).map(v=>v*sign)as[number,number,number];
     const angle=(rng()-.5)*1.20,co=pmath.cos(angle),si=pmath.sin(angle),long=(22+rng()*25)*scale,wide=(13+rng()*20)*scale,foot=(5+rng()*4)*scale;
     const lateral=loc.floorHalfM+6*scale+Math.abs(si)*long+Math.abs(co)*wide+foot;
     const base=loc.floorM,top=base+loc.depthM*(.69+rng()*.25),center=norm(loc.x+outward[0]*lateral/R,loc.y+outward[1]*lateral/R,loc.z+outward[2]*lateral/R);
     const add=(hl:number,hw:number,cap:number,shift:number,theta:number)=>{
      const c=pmath.cos(theta),sn=pmath.sin(theta),cx=center[0]+outward[0]*shift/R,cy=center[1]+outward[1]*shift/R,cz=center[2]+outward[2]*shift/R,at=norm(cx,cy,cz);
      const clip=.7071067811865476,corner=(hl+hw-(8+rng()*9)*scale)*clip;
      const planes=[c,sn,hl,-c,-sn,hl,-sn,c,hw,sn,-c,hw,(c-sn)*clip,(sn+c)*clip,corner,(-c+sn)*clip,(-sn-c)*clip,corner];
      const radiusM=Math.sqrt(hl*hl+hw*hw)+foot*1.5+2;
      // Existing public bounds must also enclose the finite projecting blocks.
      if(chord(at,f.center,R)+radiusM>f.radiusM)return;
      blocks.push(Object.freeze({center:Object.freeze(at),along:Object.freeze(along),across:Object.freeze(outward),radiusM,planes:Object.freeze(planes),baseM:base,topM:cap,footM:foot,tiltAlong:(rng()-.5)*.075,tiltAcross:(rng()-.5)*.065}));
     };
     add(long,wide,top,0,angle);
     if(rng()<.60)add(long*(1.05+rng()*.22),wide*1.17,base+loc.depthM*(.30+rng()*.23),-5*scale,angle+(rng()-.5)*.35);
    }
   }
   features[fi]=Object.freeze({...f,points:Object.freeze(points),buttresses:Object.freeze(blocks)});
  }
  Object.freeze(features);
  const mutable=new Map<number,SegmentRef[]>();
  for(const f of features)for(const seg of f.segments){
   const bound=seg.radiusM+R*(1-R/Math.sqrt(R*R+seg.radiusM*seg.radiusM)),lo=seg.center.map(v=>Math.max(0,Math.floor((v*R-bound+R)/this.cellM))),hi=seg.center.map(v=>Math.min(this.gridSize-1,Math.floor((v*R+bound+R)/this.cellM)));
   const a=f.points[seg.index],b=f.points[seg.index+1],bx=b.x-a.x,by=b.y-a.y,bz=b.z-a.z;
   const ref={f,i:seg.index,bx,by,bz,ab2:bx*bx+by*by+bz*bz};for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){const key=this.key(x,y,z);let list=mutable.get(key);if(!list)mutable.set(key,list=[]);list.push(ref);}
  }
  let maxBucket=0;for(const[key,list]of mutable){this.cells.set(key,Object.freeze(list));maxBucket=Math.max(maxBucket,list.length);}
  this.projections=new Float64Array(maxBucket*3);
  const blockIndex=new Map<number,CanyonButtress[]>();
  for(const f of features)for(const block of f.buttresses??[]){
   const bound=block.radiusM+R*(1-R/Math.sqrt(R*R+block.radiusM*block.radiusM)),lo=block.center.map(v=>Math.max(0,Math.floor((v*R-bound+R)/this.cellM))),hi=block.center.map(v=>Math.min(this.gridSize-1,Math.floor((v*R+bound+R)/this.cellM)));
   for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){const key=this.key(x,y,z);let list=blockIndex.get(key);if(!list)blockIndex.set(key,list=[]);list.push(block);}
  }
  for(const[key,list]of blockIndex)this.blockCells.set(key,Object.freeze(list));
 }
 private key(x:number,y:number,z:number):number{return(x*this.gridSize+y)*this.gridSize+z}
 sample(dx:number,dy:number,dz:number,heightM:number,out:CanyonSample):CanyonSample {
  out.heightM=heightM;out.rock=out.weight=0;if(!this.enabled)return out;
  const R=this.def.radius,c=this.cellM,key=this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)),list=this.cells.get(key);if(!list)return out;
  let floorProtection=1;
  for(let j=0;j<list.length;){
   const first=j,f=list[j].f,projection=this.projections;let bestD2=Infinity;
   while(j<list.length&&list[j].f===f){
    const ref=list[j++],a=f.points[ref.i],ax=dx-a.x,ay=dy-a.y,az=dz-a.z,bx=ref.bx,by=ref.by,bz=ref.bz;
    const raw=(ax*bx+ay*by+az*bz)/ref.ab2,t=Math.max(0,Math.min(1,raw)),px=ax-bx*t,py=ay-by*t,pz=az-bz*t,d2=px*px+py*py+pz*pz,slot=(j-1)*3;
    projection[slot]=raw;projection[slot+1]=t;projection[slot+2]=d2;bestD2=Math.min(bestD2,d2);
   }
   // Continuous compact section weights remove closest-segment Voronoi seams
   // at bends. Their width tends to zero on the shared stream centerline, and
   // remains bounded to8m on walls so the large structural planes stay legible.
   const distance=Math.sqrt(bestD2)*R,limit=distance+Math.max(.002,Math.min(8,distance*.30)),limit2=limit*limit/(R*R),scratch=this.sectionScratch;
   let sum=0,height=0,rock=0,weight=0,halfTotal=0;
   for(let k=first;k<j;k++){
    const ref=list[k],slot=k*3,raw=projection[slot],t=projection[slot+1],d2=projection[slot+2];if(d2>=limit2)continue;
    const q=limit-Math.sqrt(d2)*R,w=q*q;scratch.heightM=heightM;scratch.rock=scratch.weight=0;
    applySection(f,R,ref.i,t,raw,d2,dx,dy,dz,heightM,scratch);sum+=w;height+=scratch.heightM*w;rock+=scratch.rock*w;weight+=scratch.weight*w;
    const a=f.points[ref.i],b=f.points[ref.i+1];halfTotal+=(a.floorHalfM+(b.floorHalfM-a.floorHalfM)*t)*w;
   }
   if(sum>0)floorProtection=Math.min(floorProtection,smooth((distance-halfTotal/sum-2)/6));
   if(sum>0&&weight>0){const target=height/sum;if(out.weight===0||target<out.heightM)out.heightM=target;out.rock=Math.max(out.rock,rock/sum);out.weight=Math.max(out.weight,weight/sum);}
  }
  // Fixed-world convex masses provide real transverse end faces. They are not
  // extruded along the route, and never enter any accepted walking/stream core.
  const blocks=this.blockCells.get(key),excavated=out.heightM;
  if(out.weight>0&&floorProtection>0&&blocks)for(const block of blocks){
   const px=(dx-block.center[0])*R,py=(dy-block.center[1])*R,pz=(dz-block.center[2])*R;
   if(px*px+py*py+pz*pz>block.radiusM*block.radiusM)continue;
   const u=px*block.along[0]+py*block.along[1]+pz*block.along[2],v=px*block.across[0]+py*block.across[1]+pz*block.across[2],planes=block.planes;
   let edge=-Infinity;for(let i=0;i<planes.length;i+=3)edge=Math.max(edge,planes[i]*u+planes[i+1]*v-planes[i+2]);
   if(edge>=block.footM)continue;
   const fraction=1-Math.max(0,edge)/block.footM,cap=block.topM+u*block.tiltAlong+v*block.tiltAcross,target=Math.min(heightM,excavated+(cap-excavated)*fraction*floorProtection);
   if(target>out.heightM){out.heightM=target;out.rock=Math.max(out.rock,floorProtection*smooth(fraction/.24));}
  }
  return out;
 }
}
